import React from "react";
import { Share2, Copy, MessageCircle, Twitter, Linkedin, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { copyText } from "@/lib/clipboard";

// Reusable social / Open Graph share menu. Shares a public URL that already
// carries CiteQR Open Graph metadata for rich link previews.
export function ShareMenu({ url, shareUrl, title = "", prefix = "share", className = "", variant = "outline", size = "default", label = "Share" }) {
  const social = shareUrl || url; // rich-preview URL for social/native shares
  const t = encodeURIComponent(title || "CiteQR reference page");
  const u = encodeURIComponent(social);
  const canNativeShare = typeof navigator !== "undefined" && !!navigator.share;

  const nativeShare = async () => {
    try {
      await navigator.share({ title: title || "CiteQR", text: title || "", url: social });
    } catch (e) {
      if (e && e.name !== "AbortError") copyText(url, "Link copied to clipboard.");
    }
  };

  const open = (href) => window.open(href, "_blank", "noopener,noreferrer");

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant={variant} size={size} className={className} data-testid={`${prefix}-menu-btn`}>
          <Share2 className="mr-2 h-4 w-4" /> {label}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {canNativeShare && (
          <>
            <DropdownMenuItem onSelect={nativeShare} data-testid={`${prefix}-native`}>
              <Send className="mr-2 h-4 w-4 text-[#0D9488]" /> Share via device…
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}
        <DropdownMenuItem onSelect={() => open(`https://wa.me/?text=${t}%20${u}`)} data-testid={`${prefix}-whatsapp`}>
          <MessageCircle className="mr-2 h-4 w-4 text-[#25D366]" /> WhatsApp
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => open(`https://twitter.com/intent/tweet?text=${t}&url=${u}`)} data-testid={`${prefix}-x`}>
          <Twitter className="mr-2 h-4 w-4 text-[#1DA1F2]" /> X (Twitter)
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => open(`https://www.linkedin.com/sharing/share-offsite/?url=${u}`)} data-testid={`${prefix}-linkedin`}>
          <Linkedin className="mr-2 h-4 w-4 text-[#0A66C2]" /> LinkedIn
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => copyText(url, "Link copied to clipboard.")} data-testid={`${prefix}-copy`}>
          <Copy className="mr-2 h-4 w-4" /> Copy link
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
